import SlickSlider from 'react-slick';

// types
import type { JSX } from 'react';
import type { OKVTile } from 'components/ContentElements/OnlineCatalogAssets/types';
import type { ProductProduct } from '../reducer.types';
import { SearchIndexOff } from 'components/SearchIndex/SearchIndexOff';
import { Title } from 'components/ContentElements/Title/Title';
import { TitleFormats } from 'components/ContentElements/Title/title.types';
import { CLASS_MAPPING, slickSettings } from 'utils/slickSettings';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useTracking as useTracking2 } from 'utils/tracking/track';
import { useSectionId } from 'utils/hooks/use-section-id';

type TilesViewProps<T> = {
  componentName: string;
  renderTiles: (items: T[]) => JSX.Element | (JSX.Element | null)[];
  displayOption?: keyof typeof CLASS_MAPPING;
  title?: string;
  subtitle?: string;
  anchor?: string;
  backgroundColor?: string;
  position?: string;
  items: T[];
  slidesToScroll?: number;
  isAssortment?: boolean;
  contentIndex?: number;
  titleInAnchor?: boolean;
};

export function TilesView<T = OKVTile | ProductProduct>({
  componentName,
  displayOption,
  backgroundColor,
  title,
  anchor,
  subtitle,
  renderTiles,
  items,
  position,
  slidesToScroll = 1,
  isAssortment = false,
  titleInAnchor,
  contentIndex,
}: Readonly<TilesViewProps<T>>) {
  const tilePosition = displayOption === '1-per-row' && position === 'center' ? 'is--centered' : '';
  const sectionId = useSectionId(title ?? '', anchor, titleInAnchor, contentIndex);

  const { trackClick } = useTracking2();

  function trackSlideChange() {
    trackClick({
      click_intent: 'slider_arrow',
      click_element: 'tile',
    });
  }

  return (
    <SearchIndexOff>
      <div
        className={classNameBuilder(
          `c-${componentName}__container`,
          `bg--${backgroundColor}`,
          isAssortment && 'isAssortment',
        )}
        id={sectionId}
      >
        <div className="grid-container">
          {title && (
            <Title
              title={title}
              subtitle={subtitle}
              Format={TitleFormats.h2}
              formatClassName="h3"
              titlePreviewId="#st_title"
              subtitlePreviewId="#st_subtitle"
            />
          )}
          <div
            className={classNameBuilder(
              'c-product-tiles',
              displayOption && `c-product-tiles__${CLASS_MAPPING[displayOption]}`,
              tilePosition,
            )}
            data-preview-id={componentName === 'product-tiles' ? undefined : '#st_categories'}
          >
            {displayOption === 'carousel' ? (
              <SlickSlider
                {...slickSettings({ slides: items, afterChange: trackSlideChange })}
                slidesToScroll={slidesToScroll}
              >
                {renderTiles(items)}
              </SlickSlider>
            ) : (
              renderTiles(items)
            )}
          </div>
        </div>
      </div>
    </SearchIndexOff>
  );
}
